import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import usePageMeta from '@/hooks/usePageMeta';

const jobs = [
  {
    id: 1,
    title: 'Mechatroniker / Techniker (m/w/d)',
    type: 'Festanstellung',
    icon: 'ri-settings-3-line',
    tasks: [
      'Aufbau und Montage von Maschinen und Anlagen',
      'Unterstützung bei Inbetriebnahme und Tests',
      'Mitarbeit an individuellen technischen Lösungen',
      'Fehlerdiagnose und Wartung bestehender Anlagen',
    ],
    profile: [
      'Abgeschlossene Ausbildung als Mechatroniker oder vergleichbar',
      'Technisches Verständnis und handwerkliches Geschick',
      'Teamfähigkeit und selbstständige Arbeitsweise',
      'Führerschein Klasse B von Vorteil',
    ],
  },
  {
    id: 2,
    title: 'Elektriker / SPS-Techniker (m/w/d)',
    type: 'Festanstellung',
    icon: 'ri-cpu-line',
    tasks: [
      'Schaltschrankbau und Verdrahtung',
      'Arbeiten mit Steuerungen und SPS',
      'Unterstützung bei Automatisierungsprojekten',
      'Programmierung und Inbetriebnahme von SPS-Systemen',
    ],
    profile: [
      'Ausbildung als Elektriker, Elektroniker oder Elektrotechniker',
      'Kenntnisse in SPS-Programmierung (Siemens, Beckhoff) von Vorteil',
      'Erfahrung im Schaltschrankbau wünschenswert',
      'Strukturierte und sorgfältige Arbeitsweise',
    ],
  },
  {
    id: 3,
    title: 'Praktikum im technischen Bereich',
    type: 'Praktikum',
    icon: 'ri-graduation-cap-line',
    tasks: [
      'Einblick in Maschinenbau und Automation',
      'Mitarbeit an realen Projekten',
      'Kennenlernen verschiedener technischer Bereiche',
      'Unterstützung des Teams im Tagesgeschäft',
    ],
    profile: [
      'Studium oder Ausbildung im technischen Bereich',
      'Interesse an Maschinenbau, Elektrotechnik oder Automatisierung',
      'Eigeninitiative und Lernbereitschaft',
      'Teamgeist und Zuverlässigkeit',
    ],
  },
];

const benefits = [
  { icon: 'ri-team-line', label: 'Kleines, eingespieltes Team' },
  { icon: 'ri-focus-3-line', label: 'Abwechslungsreiche Projekte' },
  { icon: 'ri-map-pin-line', label: 'Standort Reichshof-Wehnrath' },
  { icon: 'ri-tools-line', label: 'Praxisnahe Arbeit' },
  { icon: 'ri-user-heart-line', label: 'Direkter Bezug zur Technik' },
  { icon: 'ri-time-line', label: 'Flexible Arbeitszeiten' },
];

const BewerbungsFormular = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const form = e.currentTarget;
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => params.append(key, value.toString()));
    params.append('form_type', 'bewerbung');

    setLoading(true);
    try {
      const res = await fetch('https://readdy.ai/api/form/d7f23ouivmjfhtdrfmb0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        navigate('/danke?typ=bewerbung');
      } else {
        setError('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt unter anfrage@gowitec.de.');
      }
    } catch {
      setError('Verbindungsfehler. Bitte kontaktieren Sie uns direkt unter anfrage@gowitec.de.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      id="bewerbung-gowitec"
      data-readdy-form
      onSubmit={handleSubmit}
      className="bg-white rounded-xl border border-gray-100 p-8 md:p-10"
    >
      <h3 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-3">
        <span className="w-8 h-8 flex items-center justify-center bg-[#1E3A8A]/10 rounded-lg">
          <i className="ri-send-plane-line text-[#1E3A8A]"></i>
        </span>
        Online bewerben
      </h3>
      <p className="text-gray-500 text-sm mb-7 ml-11">
        Füllen Sie das Formular aus – wir melden uns schnellstmöglich bei Ihnen.
      </p>

      {/* Honeypot */}
      <div style={{ display: 'none' }} aria-hidden="true">
        <input type="text" name="website" tabIndex={-1} autoComplete="off" />
      </div>

      {/* Stelle */}
      <div className="mb-5">
        <label htmlFor="stelle" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
          Bewerbung für <span className="text-red-500">*</span>
        </label>
        <select
          id="stelle"
          name="stelle"
          required
          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]/30 transition-colors cursor-pointer bg-white"
        >
          <option value="">Bitte wählen...</option>
          {jobs.map((j) => (
            <option key={j.id} value={j.title}>{j.title}</option>
          ))}
          <option value="Initiativbewerbung">Initiativbewerbung</option>
        </select>
      </div>

      {/* Name & Telefon */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
        <div>
          <label htmlFor="bew-name" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            id="bew-name"
            name="name"
            type="text"
            required
            placeholder="Max Mustermann"
            className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]/30 transition-colors"
          />
        </div>
        <div>
          <label htmlFor="bew-telefon" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
            Telefon
          </label>
          <input
            id="bew-telefon"
            name="telefon"
            type="tel"
            placeholder="+49 (0) 123 456789"
            className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]/30 transition-colors"
          />
        </div>
      </div>

      {/* E-Mail */}
      <div className="mb-5">
        <label htmlFor="bew-email" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
          E-Mail <span className="text-red-500">*</span>
        </label>
        <input
          id="bew-email"
          name="email"
          type="email"
          required
          placeholder="max@beispiel.de"
          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]/30 transition-colors"
        />
      </div>

      {/* Ausbildung */}
      <div className="mb-5">
        <label htmlFor="bew-ausbildung" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
          Ausbildung / Qualifikation <span className="text-red-500">*</span>
        </label>
        <input
          id="bew-ausbildung"
          name="ausbildung"
          type="text"
          required
          placeholder="z. B. Mechatroniker, Elektrotechniker, Student..."
          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]/30 transition-colors"
        />
      </div>

      {/* Motivationstext */}
      <div className="mb-5">
        <label htmlFor="bew-nachricht" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
          Kurze Vorstellung / Motivation <span className="text-red-500">*</span>
          <span className="text-gray-400 font-normal ml-2 normal-case">{charCount}/500</span>
        </label>
        <textarea
          id="bew-nachricht"
          name="nachricht"
          required
          rows={5}
          maxLength={500}
          onChange={(e) => setCharCount(e.target.value.length)}
          placeholder="Erzählen Sie uns kurz von sich, Ihrer Erfahrung und warum Sie zu gowiTEC passen..."
          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]/30 transition-colors resize-none"
        />
        <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-red-500' : 'text-gray-400'}`}>
          {charCount}/500 Zeichen
        </p>
      </div>

      {/* Hinweis Anhang */}
      <div className="bg-[#F8FAFC] border border-gray-100 rounded-lg px-4 py-3 flex items-start gap-3 mb-5 text-xs text-gray-500">
        <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
          <i className="ri-information-line text-[#1E3A8A]"></i>
        </span>
        Lebenslauf und Zeugnisse senden Sie bitte zusätzlich per E-Mail an{' '}
        <a href="mailto:anfrage@gowitec.de" className="text-[#1E3A8A] hover:underline break-all">anfrage@gowitec.de</a>
      </div>

      {/* Datenschutz */}
      <div className="flex items-start gap-3 mb-6">
        <input
          id="bew-datenschutz"
          name="datenschutz"
          type="checkbox"
          required
          className="mt-0.5 w-4 h-4 accent-[#1E3A8A] cursor-pointer shrink-0"
        />
        <label htmlFor="bew-datenschutz" className="text-xs text-gray-500 leading-relaxed cursor-pointer">
          Ich habe die{' '}
          <Link to="/datenschutz" className="text-[#1E3A8A] underline">Datenschutzerklärung</Link>{' '}
          gelesen und stimme der Verarbeitung meiner Daten zur Bearbeitung meiner Bewerbung zu. <span className="text-red-500">*</span>
        </label>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm text-red-600 flex items-center gap-2 mb-4">
          <span className="w-4 h-4 flex items-center justify-center shrink-0">
            <i className="ri-error-warning-line"></i>
          </span>
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-[#1E3A8A] hover:bg-[#1e40af] disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin"></i>
            </span>
            Wird gesendet...
          </>
        ) : (
          <>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-send-plane-fill"></i>
            </span>
            Bewerbung absenden
          </>
        )}
      </button>
    </form>
  );
};

const StellenangebotePage = () => {
  usePageMeta({
    title: 'Stellenangebote – Jobs in Maschinenbau & Automation | gowiTEC Reichshof',
    description: 'Jetzt bewerben bei gowiTEC in Reichshof-Wehnrath: Mechatroniker, SPS-Techniker und Praktikum im technischen Bereich. Inhabergeführtes Unternehmen mit 30+ Jahren Erfahrung.',
    keywords: 'Stellenangebote Maschinenbau, Jobs SPS-Techniker, Mechatroniker Reichshof, Automatisierung Ausbildung NRW',
    canonical: 'https://gowitec.de/stellenangebote',
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-24 bg-[#0f1f4a] overflow-hidden min-h-[420px] flex items-center">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20industrial%20automation%20factory%20floor%20with%20robotic%20arms%20and%20control%20panels%2C%20professional%20manufacturing%20environment%2C%20blue%20toned%20industrial%20setting%20with%20machinery%20and%20electrical%20cabinets%20in%20background%2C%20high%20quality%20corporate%20photography&width=1400&height=600&seq=stellen-hero-01&orientation=landscape"
              alt="gowiTEC – Stellenangebote Maschinenbau Automation"
              className="w-full h-full object-cover object-center"
              loading="eager"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0f1f4a] via-[#0f1f4a]/90 to-[#0f1f4a]/55" />
          </div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-0.5 bg-[#DC2626]"></span>
              <span className="text-[#DC2626] text-xs font-semibold tracking-widest uppercase">Karriere</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-5 leading-tight max-w-2xl">
              Stellenangebote bei gowiTEC
            </h1>
            <p className="text-blue-200 text-base md:text-lg max-w-xl leading-relaxed">
              Wir sind ein kleines, spezialisiertes Unternehmen im Bereich Maschinenbau, Steuerungstechnik und Automation. Wir arbeiten praxisnah, flexibel und mit direktem Bezug zur Technik.
            </p>
          </div>
        </section>

        {/* Benefits */}
        <section className="bg-[#1E3A8A] py-12">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
              {benefits.map((b) => (
                <div key={b.label} className="flex flex-col items-center text-center gap-2">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className={`${b.icon} text-white/70 text-2xl`}></i>
                  </div>
                  <span className="text-white text-xs font-medium leading-tight">{b.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Job Cards */}
        <section className="py-20 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="text-center mb-14">
              <span className="text-[#DC2626] text-xs font-semibold tracking-widest uppercase">Offene Stellen</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">Aktuelle Stellenangebote</h2>
              <p className="text-gray-500 text-base max-w-xl mx-auto leading-relaxed">
                Wir suchen engagierte Fachkräfte und Nachwuchstalente, die gemeinsam mit uns technische Lösungen entwickeln und umsetzen.
              </p>
            </div>

            <div className="flex flex-col gap-8">
              {jobs.map((job, index) => (
                <div
                  key={job.id}
                  className="bg-white rounded-xl border border-gray-100 overflow-hidden"
                >
                  {/* Job Header */}
                  <div className={`px-8 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 ${index === 0 ? 'bg-[#EFF6FF]' : ''}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#1E3A8A] rounded-xl shrink-0">
                        <i className={`${job.icon} text-white text-xl`}></i>
                      </div>
                      <div>
                        <h3 className="text-gray-900 font-bold text-xl leading-tight">{job.title}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`inline-block text-xs font-semibold px-3 py-0.5 rounded-full ${
                            job.type === 'Praktikum'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-[#EFF6FF] text-[#1E3A8A]'
                          }`}>
                            {job.type}
                          </span>
                          <span className="text-gray-400 text-xs flex items-center gap-1">
                            <i className="ri-map-pin-line"></i> Reichshof-Wehnrath
                          </span>
                        </div>
                      </div>
                    </div>
                    <a
                      href="#bewerbung"
                      className="inline-flex items-center gap-2 bg-[#1E3A8A] hover:bg-[#1e40af] text-white font-semibold px-5 py-3 rounded-md transition-colors duration-200 cursor-pointer text-sm shrink-0"
                    >
                      Jetzt bewerben
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line"></i></span>
                    </a>
                  </div>

                  {/* Job Body */}
                  <div className="px-8 py-7 grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="text-gray-900 font-semibold text-sm mb-4 flex items-center gap-2">
                        <span className="w-5 h-5 flex items-center justify-center"><i className="ri-list-check text-[#1E3A8A]"></i></span>
                        Ihre Aufgaben
                      </h4>
                      <ul className="flex flex-col gap-2.5">
                        {job.tasks.map((task) => (
                          <li key={task} className="flex items-start gap-3 text-gray-600 text-sm">
                            <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                              <i className="ri-check-line text-[#1E3A8A]"></i>
                            </span>
                            {task}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-gray-900 font-semibold text-sm mb-4 flex items-center gap-2">
                        <span className="w-5 h-5 flex items-center justify-center"><i className="ri-user-line text-[#1E3A8A]"></i></span>
                        Ihr Profil
                      </h4>
                      <ul className="flex flex-col gap-2.5">
                        {job.profile.map((p) => (
                          <li key={p} className="flex items-start gap-3 text-gray-600 text-sm">
                            <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                              <i className="ri-arrow-right-s-line text-[#DC2626]"></i>
                            </span>
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bewerbungsformular */}
        <section id="bewerbung" className="py-20 bg-[#F8FAFC] border-t border-gray-100 scroll-mt-24">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
              {/* Left Info */}
              <div className="lg:col-span-2 flex flex-col gap-6">
                <div>
                  <span className="text-[#DC2626] text-xs font-semibold tracking-widest uppercase">Bewerbung</span>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-3 mb-4">
                    Haben wir Ihr Interesse geweckt?
                  </h2>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    Bewerben Sie sich direkt hier online – schnell und unkompliziert. Wir freuen uns auf Ihre Nachricht!
                  </p>
                </div>

                <ul className="flex flex-col gap-4">
                  {[
                    { icon: 'ri-file-text-line', title: 'Formular ausfüllen', desc: 'Kurze Vorstellung und Angaben zur Qualifikation – dauert nur 2 Minuten.' },
                    { icon: 'ri-mail-send-line', title: 'Unterlagen per E-Mail', desc: 'Lebenslauf und Zeugnisse senden Sie zusätzlich an anfrage@gowitec.de.' },
                    { icon: 'ri-discuss-line', title: 'Wir melden uns', desc: 'Sie erhalten schnellstmöglich eine Rückmeldung von uns.' },
                  ].map((step, i) => (
                    <li key={step.title} className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#EFF6FF] rounded-xl shrink-0">
                        <i className={`${step.icon} text-[#1E3A8A] text-lg`}></i>
                      </div>
                      <div>
                        <div className="text-gray-900 font-semibold text-sm mb-0.5">
                          <span className="text-[#1E3A8A] mr-1">{i + 1}.</span>{step.title}
                        </div>
                        <div className="text-gray-500 text-xs leading-relaxed">{step.desc}</div>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="w-full h-48 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20electrical%20control%20cabinet%20switchgear%20panel%20with%20wiring%20and%20components%2C%20industrial%20automation%20equipment%20in%20a%20clean%20workshop%20environment%2C%20neat%20cable%20management%20and%20professional%20installation%2C%20neutral%20light%20background&width=600&height=400&seq=stellen-schrank-01&orientation=landscape"
                    alt="gowiTEC – Schaltschrankbau Arbeitsumgebung"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>

                <div className="flex items-center gap-2 text-gray-400 text-xs">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-shield-check-line text-green-500"></i>
                  </span>
                  DSGVO-konform – Ihre Daten werden vertraulich behandelt.
                </div>
              </div>

              {/* Right Form */}
              <div className="lg:col-span-3">
                <BewerbungsFormular />
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default StellenangebotePage;
